// src/lib/theme/tokens.ts
// ============================================================================
// THE DECK COMPASS: DESIGN TOKENS
// Spacing, opacity, radius, borders, transitions
// Status: LOCKED
// ============================================================================

export const tokens = {
  // ========================================================================
  // SPACING SCALE (Base 4px)
  // ========================================================================

  spacing: {
    // Micro spacing
    px: '1px',
    0.5: '2px',
    1: '4px',
    1.5: '6px',
    2: '8px',
    2.5: '10px',
    3: '12px',
    3.5: '14px',

    // Standard spacing
    4: '16px',
    5: '20px',
    6: '24px',
    7: '28px',
    8: '32px',

    // Large spacing
    9: '36px',
    10: '40px',
    12: '48px',
    14: '56px',
    16: '64px',
    20: '80px',
    24: '96px',
    28: '112px',
    32: '128px',
  },

  // ========================================================================
  // CARD PADDING (Semantic)
  // ========================================================================

  cardPadding: {
    sm: '16px', // p-4
    md: '24px', // p-6 (default)
    lg: '32px', // p-8
  },

  // ========================================================================
  // BORDER RADIUS (Sacred curves, not aggressive)
  // ========================================================================

  radius: {
    none: '0',
    sm: '4px',
    md: '8px',
    lg: '12px',
    xl: '16px',
    '2xl': '20px',
    '3xl': '24px', // Card default radius
    full: '9999px',
  },

  // ========================================================================
  // OPACITY RULES (Sacred Gold & Text Opacity)
  // ============================================================================

  opacity: {
    // Gold Border Opacity Rules (from Brand Doc + Card.tsx)
    goldDefault: 0.4, // 40% - Standard hairline borders
    goldSubtle: 0.28, // 28% - Subtle card variant
    goldOutline: 0.32, // 32% - Outline variant
    goldHover: 0.55, // 55% - Hover state emphasis
    goldFocus: 1.0, // 100% - Focus ring (full opacity)

    // Text Opacity Rules
    textPrimary: 1.0, // Full opacity
    textSecondary: 0.7, // 70% for secondary text
    textDisabled: 0.55, // 55-60% for disabled states
    textMeta: 0.7, // 70% for metadata

    // Contextual Accents (Editorial Use Only)
    accentSkyReflection: 0.55, // Max 50-60%
    accentBurntRose: 0.5, // Max 50%

    // Shadows
    shadowLight: 0.1, // Light shadow overlay
    shadowMedium: 0.12, // Medium shadow
    shadowDark: 0.12, // Dark shadow (inset)
  },

  // ========================================================================
  // BORDER STYLES
  // ========================================================================

  border: {
    // Standard hairline border (40% opacity)
    hairline: '1px solid rgba(198, 161, 74, 0.4)', // Sacred Gold @ 40%
    hairlineHover: '1px solid rgba(198, 161, 74, 0.55)', // Sacred Gold @ 55%
    hairlineSubtle: '1px solid rgba(198, 161, 74, 0.28)', // Sacred Gold @ 28%
    hairlineOutline: '1px solid rgba(198, 161, 74, 0.32)', // Sacred Gold @ 32%
  },

  // ========================================================================
  // FOCUS RING STYLE (Accessible, calm)
  // From Brand Doc: 2px ring + 2px offset
  // ========================================================================

  focus: {
    ring: '2px solid #C6A14A', // Sacred Gold @ 100%
    offset: '2px',
    outline: 'outline-offset-2px',
  },

  // ========================================================================
  // TRANSITIONS & MOTION
  // ========================================================================

  motion: {
    // Fast: 150ms (micro interactions)
    fast: '150ms cubic-bezier(0.4, 0, 0.2, 1)',

    // Normal: 300ms (standard interactions)
    normal: '300ms cubic-bezier(0.4, 0, 0.2, 1)',

    // Slow: 500ms (sacred moments)
    slow: '500ms cubic-bezier(0.4, 0, 0.2, 1)',

    // Easing function (Tailwind standard)
    easing: 'cubic-bezier(0.4, 0, 0.2, 1)',
  },

  // ========================================================================
  // INTERACTIVE STATE TRANSFORMS
  // ========================================================================

  transform: {
    // Hover lift (slight elevation)
    hoverLift: 'translateY(-2px)',

    // Active/pressed (slight press-in)
    activePress: 'translateY(1px)',

    // No transforms by default (sacred, restrained)
  },

  // ========================================================================
  // Z-INDEX SCALE (Stacking context)
  // ========================================================================

  zIndex: {
    hide: -1,
    base: 0,
    dropdown: 10,
    sticky: 20,
    fixed: 30,
    modal: 40,
    popover: 50,
    tooltip: 60,
    notification: 70,
  },

  // ========================================================================
  // GAP/SPACING BETWEEN ELEMENTS
  // ========================================================================

  gap: {
    xs: '4px',
    sm: '8px',
    md: '12px',
    base: '16px',
    lg: '24px',
    xl: '32px',
  },
} as const;

export type Tokens = typeof tokens;